import { ContentState } from "draft-js";
import { List, Map } from "immutable";
import { isArray } from "lodash";
import { SimpleMappedStore } from "@core/store/mappedStore";
import { SimpleMappedStorePresentation } from "@core/store/mappedStorePresentation";
import { SimpleStore } from "@core/store/store";
import { StorePresentation } from "@core/store/storePresentation";
import { Page } from "@utils/page";
import { CurrentProfileService } from "@domain/currentProfile/currentProfileService";
import { Post } from "@domain/post/post";
import { Category } from "@domain/lms/category/category";
import { Article, ArticleDraft, ArticleStatus } from "./article";
import { ArticleApi } from "./articleApi";
import { LightArticle } from "./dtos";
import { Feedback, FeedbackInput } from "@domain/lms/article/feedback/feedback";

export class ArticleService {
	articleStore = new SimpleStore<Article>((id) => this.articleApi.get(id));
	slugArticleStore = new SimpleStore<Article>((slug) => this.articleApi.getBySlug(slug));
	detailedArticleStore = new SimpleStore((id) => this.articleApi.getDetailed(id));
	detailedArticleStoreBySlug = new SimpleStore((slug) => this.articleApi.getDetailedBySlug(slug));
	favoriteArticlesPresentation = new StorePresentation(this.detailedArticleStore, (page) =>
		this.articleApi.getFavorites(page)
	);
	feedbackStore = new SimpleMappedStore((id, page) => this.articleApi.listFeedbacks(id, page));
	profileArticlesStore = new SimpleMappedStorePresentation(this.detailedArticleStore, (profileId, page) =>
		this.articleApi.getProfileArticles(profileId, page)
	);
	organizationArticlesStore = new SimpleMappedStorePresentation(this.detailedArticleStore, (organizationId, page) =>
		this.articleApi.getOrganizationArticles(organizationId, page)
	);
	myArticlesStore = new SimpleMappedStorePresentation(
		this.detailedArticleStore,
		(status: ArticleStatus | "ALL", page) => {
			return this.articleApi.getMyArticles(status, page);
		}
	);

	workspaceArticlesStore = new SimpleMappedStorePresentation(this.articleStore, (workspaceId, page) =>
		this.articleApi.getWorkspaceArticles(workspaceId, page)
	);

	constructor(
		private articleApi: ArticleApi,
		private currentProfileService: CurrentProfileService,
		initialArticles?: Map<string, Article>
	) {
		if (initialArticles) {
			this.detailedArticleStore.merge(List(initialArticles.values()));
		}
		this.detailedArticleStoreBySlug.bind(this.detailedArticleStore);
		// if (initialArticles) {
		// 	this.articleStore.merge(List(initialArticles.values()));
		// }
		// this.slugArticleStore.bind(this.articleStore);
	}

	async sendFeedback(articleId: string, feedback: FeedbackInput): Promise<void> {
		await this.articleApi.sendFeedback(articleId, feedback);
		await this.feedbackStore.fetch(articleId);
		await this.detailedArticleStore.fetch(articleId);
	}

	async deleteFeedback(articleId: string, articleFeedbackId: string): Promise<void> {
		await this.articleApi.deleteFeedback(articleId, articleFeedbackId);
		await this.feedbackStore.fetch(articleId);
		await this.detailedArticleStore.fetch(articleId);
	}

	async updateFeedback(articleId: string, articleFeedbackId: string, feedback: FeedbackInput): Promise<void> {
		await this.articleApi.updateFeedback(articleId, articleFeedbackId, feedback);
		await this.feedbackStore.fetch(articleId);
		await this.detailedArticleStore.fetch(articleId);
	}

	async toggleFavorite(article: Article): Promise<void> {
		if (!article.isFavorite) {
			await this.articleApi.setFavorite(article.id);
		} else {
			await this.articleApi.removeFavorite(article.id);
		}
		this.articleStore.update(article.id, (c) => ({ ...c, isFavorite: !article.isFavorite }));
		this.detailedArticleStore.update(article.id, (c) => ({ ...c, isFavorite: !article.isFavorite }));
	}

	async toggleInstructive(article: Article): Promise<void> {
		const initialInstructive = article.instructives.byMe;

		const articleIntructif = (article: Article): Article => {
			return {
				...article,
				instructives: { byMe: !initialInstructive, total: article.instructives.total + (initialInstructive ? -1 : 1) },
			};
		};

		this.detailedArticleStoreBySlug.update(article.slug, (art) => articleIntructif(art));
		this.detailedArticleStore.update(article.id, (art) => articleIntructif(art));

		await (initialInstructive
			? this.articleApi.removeInstructive(article.id)
			: this.articleApi.setInstructive(article.id));
	}

	async getMyFeedback(articleId: string, articleFeedbackId: string): Promise<Feedback | null> {
		try {
			return await this.articleApi.getMyFeedback(articleId, articleFeedbackId);
		} catch (e) {
			if (e instanceof Error && "code" in e && e.code === 404) {
				return null;
			}
			throw e;
		}
	}

	async shareArticle(articleId: string, message?: ContentState, groupId?: string): Promise<Post> {
		return this.articleApi.share(articleId, message, groupId);
	}

	async saveArticle(draft: ArticleDraft, id?: string, authorId?: string): Promise<LightArticle> {
		const article = await (id
			? this.articleApi.saveArticle(draft, id, authorId)
			: this.articleApi.createArticle(draft, authorId));
		return article;
	}

	async deleteArticle(articleId: string): Promise<void> {
		await this.articleApi.delete(articleId);
		this.detailedArticleStore.remove(articleId);
	}

	suggest = (count: number, cat: Category | Category[] | undefined = undefined): Promise<Page<Article>> => {
		const categories: Category[] = cat
			? isArray(cat)
				? cat
				: [cat]
			: (this.currentProfileService.currentProfile.get()?.categories ?? []);
		return this.articleApi.getSuggestions({ page: 0, size: count }, categories);
	};
	newsArticles = (count: number, page: number): Promise<Page<Article>> => {
		// const categories: Category[] = cat
		// 	? isArray(cat)
		// 		? cat
		// 		: [cat]
		// 	: this.currentProfileService.currentProfile.get()?.categories ?? [];
		return this.articleApi.getNewsArticles(count, page);
	};

	search = this.articleApi.search.bind(this.articleApi);

	view(articleId: string): Promise<void> {
		return this.articleApi.view(articleId);
	}

	incrementView(article: Article): Promise<void> {
		return this.articleApi.view(article.id);
	}
}
